import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface TabDef {
  readonly id: string;
  readonly label: string;
  readonly badge?: ReactNode;
  readonly testId?: string;
}

export interface TabsProps {
  readonly tabs: readonly TabDef[];
  readonly activeId: string;
  readonly onSelect: (id: string) => void;
  readonly testId?: string;
}

export function Tabs({ tabs, activeId, onSelect, testId }: TabsProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  // Phones: the strip scrolls sideways when the tabs do not fit. Keep the
  // active tab inside the strip by moving ONLY the strip's own scrollLeft
  // (never scrollIntoView, which would also scroll the page vertically).
  useEffect(() => {
    const list = listRef.current;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const active = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    const left = active.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
    const right = left + active.getBoundingClientRect().width;
    if (left < list.scrollLeft) list.scrollLeft = Math.max(0, left - 16);
    else if (right > list.scrollLeft + list.clientWidth) list.scrollLeft = right - list.clientWidth + 16;
  }, [activeId]);

  return (
    <div
      ref={listRef}
      role="tablist"
      className="ui-tabs"
      data-testid={testId ?? 'tabs'}
      style={{
        display: 'flex',
        gap: 2,
        borderBottom: '1px solid var(--border-subtle)',
        marginBottom: 16,
        overflowX: 'auto',
      }}
    >
      {tabs.map((t) => {
        const active = t.id === activeId;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={active}
            data-testid={t.testId ?? `tab-${t.id}`}
            onClick={() => onSelect(t.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderBottom: active
                ? '2px solid var(--accent, #2563eb)'
                : '2px solid transparent',
              background: 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
              fontWeight: active ? 600 : 500,
              fontSize: 13.5,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: -1,
            }}
          >
            {t.label}
            {t.badge ? <span style={{ marginLeft: 6 }}>{t.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
